import React, { useMemo } from "react";
import { View, StyleSheet } from "react-native";
import { useNavigation, type NavigationProp } from "@react-navigation/native";
import { ThumbsUp, ThumbsDown } from "lucide-react-native";
import {
  QuestionLayout,
  SquareOptionButton,
} from "@src/packages/medical-history/components";
import { useTranslations } from "@src/packages/i18n";
import { useMenstrualHealth } from "../MenstrualHealthContext";
import { buildMenstrualHealthNavigationStack } from "../buildMenstrualHealthNavigationStack";
import type { MenstrualHealthStackParamList } from "../MenstrualHealthNavigator";

export function FertileWindowScreen() {
  const t = useTranslations();
  const { state, updateState, save } = useMenstrualHealth();
  const navigation =
    useNavigation<NavigationProp<MenstrualHealthStackParamList>>();

  const selection = useMemo(() => {
    if (state.awareOfFertileWindow == null) return null;
    return state.awareOfFertileWindow ? "yes" : "no";
  }, [state.awareOfFertileWindow]);

  const handleChange = (value: "yes" | "no") => {
    const stateUpdates = { awareOfFertileWindow: value === "yes" };
    updateState(stateUpdates);

    if (selection === null) {
      void save(stateUpdates).catch(() => undefined);
      const nextState = { ...state, ...stateUpdates };
      const stack = buildMenstrualHealthNavigationStack(nextState);
      const currentIdx = stack.indexOf("FertileWindow");
      const nextRoute = stack[currentIdx + 1];
      if (nextRoute) {
        navigation.navigate(nextRoute);
      }
    }
  };

  const handleSave = async () => {
    await save();
    const stack = buildMenstrualHealthNavigationStack(state);
    const currentIdx = stack.indexOf("FertileWindow");
    const nextRoute = stack[currentIdx + 1];
    if (nextRoute) {
      navigation.navigate(nextRoute);
    }
  };

  return (
    <QuestionLayout
      title={t("med_history.gynae.menstrual_health.fertile_window_title")}
      subtitle={t("med_history.gynae.menstrual_health.fertile_window_subtitle")}
      onBack={() => navigation.goBack()}
      onSave={handleSave}
      saveDisabled={selection === null}
      hideSaveButton={selection === null}
      saveLabel={t("med_history.gynae.menstrual_health.continue")}
    >
      <View style={styles.optionsContainer}>
        <SquareOptionButton
          icon={ThumbsUp}
          label={t("med_history.shared.yes")}
          isSelected={selection === "yes"}
          onPress={() => handleChange("yes")}
          iconSize={24}
          containerSize={48}
          labelSize="lg"
        />
        <SquareOptionButton
          icon={ThumbsDown}
          label={t("med_history.shared.no")}
          isSelected={selection === "no"}
          onPress={() => handleChange("no")}
          iconSize={24}
          containerSize={48}
          labelSize="lg"
        />
      </View>
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  optionsContainer: {
    flexDirection: "row",
    gap: 12,
    marginBottom: 40,
    width: "100%",
  },
});
